import { useEffect, useMemo, useState } from "react";
import Page from "../Components/Page";
import { axiosInstance } from "../Components/axiosInstance.js";
import AggregatorSelectedGrowerPanel from "../Components/AggregatorSelectedGrowerPanel.jsx";
import { WorkspaceState } from "../Components/GrowerLayout.jsx";
import {
  readSelectedAggregatorGrower,
  writeSelectedAggregatorGrower,
} from "../portal/aggregatorGrowerContext.js";

function unwrapRows(payload) {
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }
  if (Array.isArray(payload)) {
    return payload;
  }
  return [];
}

const SCOPE_LABELS = {
  "data.read": "View records",
  "data.write": "Update records",
  "import.write": "Run imports",
  "reconciliation.write": "Resolve conflicts",
  all: "All permissions",
};

function growerLabel(row) {
  return (
    row.grower_display_name ||
    row.grower_primary_contact_email ||
    row.grower_profile_public_id ||
    row.grower_email
  );
}

export default function AggregatorGrowersPage() {
  const [delegations, setDelegations] = useState([]);
  const [selectedGrower, setSelectedGrower] = useState(() =>
    readSelectedAggregatorGrower()
  );
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let isMounted = true;

    async function loadDelegations() {
      setIsLoading(true);
      setErrorMessage("");
      try {
        const response = await axiosInstance.get("/delegations/received");
        if (isMounted) {
          setDelegations(unwrapRows(response.data));
        }
      } catch (error) {
        if (isMounted) {
          setDelegations([]);
          setErrorMessage("Grower delegations could not be loaded.");
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadDelegations();

    return () => {
      isMounted = false;
    };
  }, []);

  const activeDelegations = useMemo(
    () =>
      delegations.filter(
        (item) =>
          item.status === "ACTIVE" &&
          !item.revoked_at &&
          item.grower_profile_public_id
      ),
    [delegations]
  );

  function selectGrower(row) {
    const nextGrower = {
      delegation_public_id: row.public_id,
      grower_profile_public_id: row.grower_profile_public_id,
      label: growerLabel(row),
      scope: row.scope || [],
    };
    writeSelectedAggregatorGrower(nextGrower);
    setSelectedGrower(nextGrower);
  }

  function clearSelection() {
    writeSelectedAggregatorGrower(null);
    setSelectedGrower(null);
  }

  return (
    <Page
      title="Growers"
      headerBorderColor="border-[#34a853]"
      headerVariant="hybrid"
      contentWidth="wide"
      padding="p-4 md:p-6"
      introContent="Select an authorized grower context before imports, reconciliation, or record updates."
    >
      <div className="flex w-full flex-col gap-5 text-[#5c6673]">
        {selectedGrower ? (
          <AggregatorSelectedGrowerPanel
            onClear={clearSelection}
            selectedGrower={selectedGrower}
          />
        ) : null}

        <div className="rounded-[8px] border border-slate-200 bg-white shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
          <div className="flex items-center justify-between gap-4 border-b border-slate-200 px-5 py-4">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.14em] text-[#75839a]">
                Active Grower Access
              </p>
              <p className="mt-1 text-sm text-slate-500">
                {activeDelegations.length} authorized {activeDelegations.length === 1 ? "grower" : "growers"}
              </p>
            </div>
          </div>
          {errorMessage ? (
            <WorkspaceState
              state="error"
              title={errorMessage}
              description="Refresh the page or ask an administrator to confirm the delegation."
            />
          ) : isLoading ? (
            <WorkspaceState state="loading" title="Loading grower access" />
          ) : activeDelegations.length === 0 ? (
            <WorkspaceState
              title="No active grower delegations"
              description="An administrator must link a grower before records or imports can be opened."
            />
          ) : (
            <div className="divide-y divide-slate-200">
              {activeDelegations.map((row) => {
                const isSelected =
                  selectedGrower?.grower_profile_public_id ===
                  row.grower_profile_public_id;
                return (
                  <div
                    className="grid gap-4 p-5 md:grid-cols-[minmax(0,1fr)_180px]"
                    data-testid={`grower-access-${row.public_id}`}
                    key={row.public_id}
                  >
                    <div>
                      <p className="text-lg font-semibold text-[#243245]">
                        {growerLabel(row)}
                      </p>
                      <p className="mt-1 text-sm text-[#75839a]">
                        {row.grower_profile_public_id}
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold text-[#5c6673]">
                        {(row.scope?.length ? row.scope : ["all"]).map((scope) => (
                          <span className="rounded-[4px] border border-slate-200 bg-slate-50 px-2.5 py-1" key={scope}>
                            {SCOPE_LABELS[scope] || String(scope).replace(".", " ")}
                          </span>
                        ))}
                        <span className="rounded-[4px] border border-slate-200 bg-slate-50 px-2.5 py-1">
                          {row.link_type}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-start md:justify-end">
                      <button
                        className={`min-w-24 rounded-[4px] px-4 py-2 text-sm font-semibold ${
                          isSelected
                            ? "border border-[#34a853] bg-[#eef8f0] text-[#1f6d38]"
                            : "bg-[#243245] text-white"
                        }`}
                        onClick={() => selectGrower(row)}
                        type="button"
                      >
                        {isSelected ? "Selected" : "Select"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Page>
  );
}
